import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import {
  Activity, FileText, Ticket, Shield, Users,
  RefreshCw, Circle, CheckCircle, XCircle, Clock,
  ArrowRight, AlertTriangle
} from 'lucide-react';

const eventIcons = {
  contract_workflow: FileText,
  ticket: Ticket,
  audit: Shield,
};

const statusColors = {
  Approved: 'bg-emerald-500',
  Declined: 'bg-red-500',
  Terminated: 'bg-gray-500',
  Requested: 'bg-sky-500',
  Pending_Carrier: 'bg-amber-500',
  Missing_Info: 'bg-orange-500',
  Submitted_To_Carrier: 'bg-indigo-500',
  On_Hold: 'bg-yellow-500',
  Open: 'bg-blue-500',
  In_Progress: 'bg-amber-500',
  Closed: 'bg-gray-400',
};

export function ActivityFeed({ autoRefresh = true, refreshInterval = 30000 }) {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadFeed = useCallback(async () => {
    try {
      const res = await dashboardAPI.activityFeed(25);
      setFeed(res.data || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to load activity feed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFeed();
    if (autoRefresh) {
      const interval = setInterval(loadFeed, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [loadFeed, autoRefresh, refreshInterval]);

  const handleRefresh = () => {
    setLoading(true);
    loadFeed();
  };

  const getEventIcon = (event) => {
    const Icon = eventIcons[event.event_type] || Activity;
    return <Icon size={14} />;
  };

  const getStatusDot = (status) => {
    const color = statusColors[status] || 'bg-slate-400';
    return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
  };

  const getEventLink = (event) => {
    if (event.entity_type === 'contract' && event.entity_id) {
      return `/requests/${event.entity_id}`;
    }
    if (event.entity_type === 'ticket' && event.entity_id) {
      return `/tickets?id=${event.entity_id}`;
    }
    if (event.entity_type === 'agent' && event.entity_id) {
      return `/agents/${event.entity_id}`;
    }
    if (event.entity_type === 'agency' && event.entity_id) {
      return `/agencies/${event.entity_id}`;
    }
    return null;
  };

  return (
    <Card className="animate-slideIn" data-testid="activity-feed-card">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Activity size={16} className="text-sky-500" />
          Live Activity Feed
          {feed.length > 0 && (
            <Badge className="bg-sky-100 text-sky-700 text-[10px]">{feed.length}</Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          {lastRefresh && (
            <span className="text-[10px] text-slate-400">
              {formatDistanceToNow(lastRefresh, { addSuffix: true })}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            className="h-7 w-7 p-0"
            data-testid="activity-feed-refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin text-sky-500' : 'text-slate-400'} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ScrollArea className="h-[400px] pr-2">
          {loading && feed.length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-400">
              <RefreshCw size={20} className="animate-spin mr-2" />
              Loading activity...
            </div>
          ) : feed.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <Activity size={32} className="mb-2" />
              <p className="text-sm">No recent activity</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[11px] top-2 bottom-2 w-px bg-slate-100" />

              {feed.map((event, i) => {
                const link = getEventLink(event);
                const Wrapper = link ? Link : 'div';
                const wrapperProps = link ? { to: link } : {};

                return (
                  <Wrapper
                    key={event.event_id || i}
                    {...wrapperProps}
                    className="relative flex items-start gap-3 py-2.5 px-1 group hover:bg-slate-50/50 rounded-lg transition-colors"
                    data-testid={`activity-item-${i}`}
                  >
                    {/* Timeline dot */}
                    <div className="relative z-10 mt-1 w-[22px] h-[22px] rounded-full bg-white border-2 border-slate-200 flex items-center justify-center shrink-0 group-hover:border-sky-300 transition-colors">
                      <span className="text-slate-500">{getEventIcon(event)}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-medium text-slate-800 truncate">
                          {event.details || event.action?.replace(/_/g, ' ')}
                        </p>
                        {event.status && getStatusDot(event.status)}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-slate-400">
                          {event.performed_by || 'System'}
                        </span>
                        <span className="text-[10px] text-slate-300">·</span>
                        <span className="text-[10px] text-slate-400">
                          {event.timestamp
                            ? formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })
                            : ''}
                        </span>
                      </div>
                    </div>

                    {link && (
                      <ArrowRight size={12} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity mt-1 shrink-0" />
                    )}
                  </Wrapper>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
